/* eslint-disable import/no-cycle */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@glu/theming';

// glu
import { Dropdown } from '@glu/dropdown-react';

// lodash
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';

import styles from './bankCodeArea.styles';

// components
import RadioButton from '../radioButton/RadioButton';

// utils
import { buildDropdownProps, getPaymentType } from '../createPayment/CreatePayment.utils';
import { buildBankCodes } from './utils';
import AppPropTypes from '../../proptypes/AppPropTypes';

// services
import { getBankCodes } from '../../api/payments/service';

const mappings = {
  PAYMENT_TYPES: {
    Domestic: 'D',
    International: 'I'
  },
  PAYMENT_CODES: {
    D: 'domestic',
    I: 'international'
  }
};

function BankCodeAreaComponent({
  formChange,
  formState,
  field,
  locale,
  classes,
  type,
  fromTemplate,
  onLoad,
  required,
  data = {},
  asRow,
  children,
  preloaded,
  isTemplate
}) {
  const { addresses, bankDropDown, bankDropDownInline, typeofBankCodes } = classes;

  const [radioDetails, setRadioDetails] = useState({});
  const [bankCodes, setBankCodes] = useState({});
  const [bankCodesPayload, setBankCodesPayload] = useState({});
  const [bankCodesDetails, setBankCodesDetails] = useState({});
  const [state, setState] = useState({});

  const radio = { name: `typeOf${capitalize(field)}` };

  /**
   * Provides a collection of services, that we are going to load
   */
  const getService = (matchBy) => {
    const method = formState[field] && get(formState, `${field}.0.paymentMethod`);
    const mappedMethod = method === 'FED' ? 'domestic' : 'international';
    const paymentType =
      get(bankCodesPayload, `${field}.paymentMethod`) ||
      (method && mappedMethod) ||
      (fromTemplate || preloaded || isTemplate
        ? get(formState, 'paymentType.0.type', get(formState, 'paymentType'))
        : get(
            formState,
            'paymentType[0].type',
            getPaymentType(formState, data.paymentTypeOptions, { template: isTemplate })
          ));

    const bankCodes$ = getBankCodes({
      paymentMethod: Array.isArray(paymentType)
        ? paymentType?.[0]?.id?.toLowerCase()
        : paymentType?.toLowerCase(),
      page: get(bankCodesPayload, `${field}.page`) || 1,
      match: matchBy || bankCodesPayload?.[field]?.matchBy
    });

    bankCodes$.subscribe((result) => {
      const routingNumbers = (bankCodes[field] || []).map((x) => x.routingNumber);
      const removeDuplicated = result.filter(
        (x) => !routingNumbers.find((number) => x.routingNumber === number)
      );

      setBankCodes((cd) => {
        const match = removeDuplicated.find((x) =>
          [x.routingNumber, x.bankCode].includes(get(formState[field], '0.id'))
        );

        setBankCodesDetails((details) => ({
          ...details,
          [field]: match
        }));

        return {
          ...cd,
          [field]: uniqBy(
            [...(cd[field] || []), ...removeDuplicated],
            (x) => x.bankCode || x.routingNumber
          )
        };
      });

      setState((state) => ({
        ...state,
        loading: false,
        searching: false
      }));
    });
  };

  useEffect(() => {
    setBankCodesPayload((payload) => ({
      ...payload,
      [field]: {
        page: 1,
        matchBy: ''
      }
    }));

    if (formState[field]) {
      const matchBy = get(formState, `${field}.0.id`);
      getService(matchBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bankCodesPayload[field]) {
      getService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCodesPayload]);

  useEffect(() => {
    onLoad({ bankCodes });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCodes]);

  const onNextPage = () => {
    setState((state) => ({ ...state, searching: true }));
    setBankCodesPayload((payload) => ({
      ...payload,
      [field]: { ...payload[field], page: payload[field].page + 1 }
    }));
  };

  const onFilterBankCode = (string) => {
    setBankCodesPayload((payload) => ({
      ...payload,
      [field]: { ...payload[field], matchBy: string, page: 1 }
    }));
    setState((state) => ({ ...state, searching: true }));
  };

  const method = formState[field] && get(formState, `${field}.0.paymentMethod`);
  const mappedMethod = method === 'FED' ? 'D' : 'I';
  const paymentType = get(
    formState,
    'paymentType[0].type',
    getPaymentType(formState, data.paymentTypeOptions, { template: isTemplate })
  );
  const isInternational = paymentType === 'International';

  const dropdown = () => (
    <div className={classnames({ 'col-xs-3': isInternational || asRow })}>
      <Dropdown
        {...buildDropdownProps(locale, {
          rootClassName: classnames({ [classes.align]: isInternational && !asRow }),
          field,
          htmlId: field,
          labelText: locale['paymentAdditionalDetails.bankCode'],
          isLoading: state.searching || state.loading,
          onChange: (name, value) => {
            formChange(name, value);

            const instance = bankCodes[field];

            if (instance) {
              const match = bankCodes[field].find((x) => x.routingNumber === get(value, '0.id'));
              setBankCodesDetails((details) => ({
                ...details,
                [field]: match
              }));

              setRadioDetails((details) => ({ ...details, [radio.name]: 'I' }));
            }
          },
          onFilter: (string) => onFilterBankCode(string),
          onNextPage,
          showBadges: !fromTemplate,
          formGroup: false,
          type: type.toLowerCase(),
          options: buildBankCodes(bankCodes[field] || []),
          validators: [{ required, msg: locale['validation.required'] }],
          stretch: true,
          defaultValue: get(formState, `${field}`),
          disabled: fromTemplate || state.loading
        })}
      />
      {formState[field] && formState[field].length > 0 && bankCodesDetails[field] && (
        <div className={classnames(addresses)}>
          <span>{bankCodesDetails[field].address1}</span>
          <span>{bankCodesDetails[field].address2}</span>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={classnames({
        [bankDropDownInline]: true,
        [bankDropDown]: asRow,
        international: isInternational,
        'col-xs-3': !asRow && !isInternational
      })}
    >
      {isInternational && (
        <RadioButton
          name={radio.name}
          disabled={fromTemplate || state.loading}
          value={
            (method && mappedMethod) ||
            radioDetails[radio.name] ||
            mappings.PAYMENT_TYPES[paymentType]
          }
          data={[
            { value: 'D', label: 'Domestic List' },
            { value: 'I', label: 'International List' }
          ]}
          onUpdate={({ value }) => {
            setState((state) => ({ ...state, loading: true }));
            setRadioDetails((rad) => ({ ...rad, [radio.name]: value }));
            setBankCodes((codes) => ({ ...codes, [field]: [] }));

            if (radioDetails[radio.name]) {
              setBankCodesPayload((payload) => ({
                ...payload,
                [field]: { page: 1, paymentMethod: mappings.PAYMENT_CODES[value] }
              }));
              setBankCodesDetails((details) => ({ ...details, [field]: null }));
              formChange(field, []);
            }
          }}
          settings={{ titleOnTop: true }}
          className={classnames(typeofBankCodes)}
          title="select bank code type"
          styled
        />
      )}
      {asRow && (
        <div className={classes.row}>
          {dropdown()}
          {children()}
        </div>
      )}

      {!asRow && dropdown()}
    </div>
  );
}

BankCodeAreaComponent.defaultProps = {
  required: false,
  asRow: false,
  children() {},
  preloaded: false,
  data: null
};

BankCodeAreaComponent.propTypes = {
  formChange: PropTypes.func.isRequired,
  formState: PropTypes.shape({}).isRequired,
  field: PropTypes.string.isRequired,
  locale: AppPropTypes.locale.isRequired,
  classes: AppPropTypes.jssClasses.isRequired,
  type: PropTypes.string.isRequired,
  fromTemplate: PropTypes.bool.isRequired,
  onLoad: PropTypes.func.isRequired,
  required: PropTypes.bool,
  asRow: PropTypes.bool,
  children: PropTypes.func,
  data: PropTypes.shape({}),
  preloaded: PropTypes.bool,
  isTemplate: PropTypes.bool.isRequired
};

export default withStyles(styles)(BankCodeAreaComponent);
